import { useState, useEffect } from 'react';
import axios from 'axios';
import type { StaffHeadcountRow } from '../components/profitAndLoss/constants';
import type { BranchDeptLogic } from '../components/ProfitAndLossSearchFilters';
import { ep } from '../api/endpoints';

type UseStaffHeadcountParams = {
  fiscalYear: number;
  branchCodes: string[];
  departmentCodes: string[];
  // 拠点×部署の結合ロジック。損益側の絞り込みと同じスコープになるよう共有する
  branchDeptLogic: BranchDeptLogic;
};

/**
 * 従業員 月次在籍数推移のデータ層フック（経営概況ページ専用）。
 *
 * 損益本体とは独立して取得し、失敗してもページは壊さない。営業ページでは本フックを
 * 呼ばないことで不要なAPI取得を発生させない。
 * 在籍数テーブルの支店行・部署別ドリルダウンの開閉 state もここで保持する。
 */
export function useStaffHeadcount({ fiscalYear, branchCodes, departmentCodes, branchDeptLogic }: UseStaffHeadcountParams) {
  const [staffRows, setStaffRows] = useState<StaffHeadcountRow[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);

  // 月次在籍数推移テーブルの支店行の開閉 (部門ドリルダウン)
  const [expandedStaffBranches, setExpandedStaffBranches] = useState<Record<string, boolean>>({});
  const toggleStaffBranch = (branchCode: string) => {
    setExpandedStaffBranches(prev => ({ ...prev, [branchCode]: !prev[branchCode] }));
  };

  // 在籍数行の部署別ドリルダウンの開閉 (エンティティ label 単位)
  const [expandedHeadcountDepts, setExpandedHeadcountDepts] = useState<Record<string, boolean>>({});
  const toggleHeadcountDepts = (label: string) => {
    setExpandedHeadcountDepts(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // 従業員 月次在籍数推移の取得（年度・拠点・部署変更時）
  useEffect(() => {
    const fetchStaffHeadcount = async () => {
      setStaffLoading(true);
      try {
        const response = await axios.post(
          ep('get_staff_headcount_trend'),
          {
            fiscal_year: fiscalYear,
            branch_codes: branchCodes.length > 0 ? branchCodes : null,
            department_codes: departmentCodes.length > 0 ? departmentCodes : null,
            branch_dept_logic: branchDeptLogic,
            // 雇用形態別 (社員/契約/パート/派遣 等) の内訳表示に使うため支店×部門×雇用形態で取得する
            include_employment_status: true,
          }
        );
        if (response.data.status === 'success') {
          setStaffRows(response.data.data || []);
        } else {
          setStaffRows([]);
        }
      } catch (err) {
        console.error('従業員在籍数推移の取得エラー:', err);
        setStaffRows([]);
      } finally {
        setStaffLoading(false);
      }
    };
    fetchStaffHeadcount();
  }, [fiscalYear, branchCodes, departmentCodes, branchDeptLogic]);

  return {
    staffRows,
    staffLoading,
    expandedStaffBranches,
    toggleStaffBranch,
    expandedHeadcountDepts,
    toggleHeadcountDepts,
  };
}

export type UseStaffHeadcountResult = ReturnType<typeof useStaffHeadcount>;
